import * as amplitude from '@amplitude/analytics-browser';
import React, { useEffect } from 'react';
import axios from 'axios';
import './utils/firebaseInit'; // import before TopBar which calls getAuth
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { AppStore } from './AppStore';
import ContentSwitcher from './components/ContentSwitcher';
import { initContentData, initLoggedInPuzzleSolveStates } from './utils/initUtils';
import { loadOrResetLocalStorage } from './utils/persistenceUtils';
import { ContentData, LoadUserResponse, PuzzleData, PuzzleNames } from './utils/types';
import { PathwordStore } from './components/_puzzles/Pathword/PathwordStore';
import { Pathword5x5Store } from './components/_puzzles/Pathword5x5/Pathword5x5Store';
import { getEnabledHintsForPathword, getEnabledHintsForPathword5x5 } from './components/_puzzles/Pathword/pathwordUtils';
import './styles.less';

try {
  amplitude.init('75831f161966b62fc4fa9f037b28f323', {
    defaultTracking: true,
    // equivalent to LogLevel.None in their enum that isnt available in frontend sdk.
    // without this, it spews errors and warnings when its requests are blocked by ublock/vpn.
    // the errors then trigger the react dev error overlay constantly, which is infuriating.
    // it happens even if you catch the error. disabling the logging is the only way to stop it,
    // because the overlay is monitoring logging i guess.
    logLevel: 0,
  });
} catch (error) { /* just in case */ }

const App = () => {
  useEffect(() => {
    const auth = getAuth();
    
    // we wait for firebase to establish login state before loading our own data
    onAuthStateChanged(auth, async (user) => {
      // load puzzles
      let puzzlesLoaded = false;
      const puzzleIds: any = {};
      try {
        // Include auth token if user is logged in (for analytics)
        const headers = user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {};
        const response = await axios.get<ContentData>(`/api/puzzles`, { headers });
        const { featureData, puzzleData } = response.data;
        initContentData(puzzleData, featureData);
        puzzlesLoaded = true;

        Object.keys(puzzleData).forEach(key => {
          puzzleIds[key] = puzzleData[key as keyof PuzzleData].puzzleId;
        });
        // temporary while truthsortingV1 is being deprecated; full removal pending
        puzzleIds[PuzzleNames.Truthsorting] = puzzleData['truthsortingV2'].puzzleId;
        window.scrollTo({ top: 0 });
      } catch (error) {
        console.error('Error fetching puzzle data:', error);
        AppStore.update(s => {
          s.loadingContentError = true;
          s.loadingContent = false;
        });
      }

      // don't even try loading user if we couldn't load puzzles.
      if (!puzzlesLoaded) {
        AppStore.update(s => {
          s.loadingUser = false;
          s.loadingUserError = false;
          s.signedIn = false;
          s.userData = null;
        });

        return;
      }

      // load user
      if (user) {
        const authToken = await user.getIdToken(true);

        try {
          const response = await axios.post<LoadUserResponse>(
            '/api/users/load-user',
            { authToken, puzzleIds },
          );
          AppStore.update(s => {
            s.loadingUser = false;
            s.loadingUserError = false;
            s.signedIn = true;
            s.userData = {
              stats: response.data.stats,
              settings: response.data.settings,
            };
          });
          initLoggedInPuzzleSolveStates(response.data.puzzlesProgress);

          // Update pathword puzzles with user's hint settings
          PathwordStore.update(s => {
            s.enabledHints = getEnabledHintsForPathword(response.data.settings.pathwordDefaultHints);
            s.initialEnabledHints = response.data.settings.pathwordDefaultHints;
          });
          Pathword5x5Store.update(s => {
            s.enabledHints = getEnabledHintsForPathword5x5(response.data.settings.pathword5x5DefaultHints);
            s.initialEnabledHints = response.data.settings.pathword5x5DefaultHints;
          });
        } catch (error) {
          AppStore.update(s => {
            s.loadingUser = false;
            s.loadingUserError = true;
            s.signedIn = false;
            s.userData = null;
          });
          loadOrResetLocalStorage(puzzleIds);
          auth.signOut();
          alert('Something went wrong while loading account. Try refreshing and logging in again.');
        }
      } else {
        loadOrResetLocalStorage(puzzleIds);

        AppStore.update(s => {
          s.loadingUser = false;
          s.loadingUserError = false;
          s.signedIn = false;
          s.userData = null;
        });
      }
    });
  }, []);

  return (
    <ContentSwitcher/>
  );
};

export default App;